import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useSWRConfig } from 'swr';

import { cx } from '../../../shared/lib/cx.js';
import forms from '../../../shared/styles/forms.module.css';
import { ErrorState } from '../../../shared/ui/ErrorState.js';
import { LoadingState } from '../../../shared/ui/LoadingState.js';
import { ACCOUNTS_KEY } from '../../accounts/hooks/useAccounts.js';
import { CATEGORIES_KEY } from '../../categories/hooks/useCategories.js';
import { useCurrentContext } from '../../identity/hooks/useCurrentContext.js';
import { ExpenseForm } from '../components/ExpenseForm.js';
import { IncomeForm } from '../components/IncomeForm.js';
import { SplitForm } from '../components/SplitForm.js';
import { TransferForm } from '../components/TransferForm.js';
import { useQuickAddOptions } from '../hooks/useQuickAdd.js';
import styles from './QuickAddPage.module.css';

type Kind = 'expense' | 'income' | 'transfer' | 'split';

const TABS: ReadonlyArray<readonly [Kind, string]> = [
  ['expense', 'Chi tiêu'],
  ['income', 'Thu nhập'],
  ['transfer', 'Chuyển tiền'],
  ['split', 'Chia tiền'],
];

/**
 * `?loai=thu` opens on the income tab. The dashboard's empty state has a button that says
 * "Nhập giao dịch thu", and a button must do what it says — landing on the expense tab would
 * make the user's first action undoing the app's choice.
 */
const KIND_BY_PARAM: Record<string, Kind> = {
  chi: 'expense',
  thu: 'income',
  'chuyen-tien': 'transfer',
  'chia-tien': 'split',
};

/**
 * Quick Add — feature number one, measured in seconds (spec v2 22.1, plan step 1.2).
 *
 * A full-screen sheet rather than a screen inside the app shell: no bottom navigation to
 * mis-tap while the keyboard is up, and the ✕ returns to wherever it was opened from.
 *
 * After a save the form is remounted empty instead of navigating away — the common case is
 * several entries in a row (three receipts on the kitchen table), and a screen change would
 * cost a page load and the keyboard each time.
 */
export function QuickAddPage() {
  const contextState = useCurrentContext();
  const optionsState = useQuickAddOptions();
  const { mutate } = useSWRConfig();
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  // Seeded once: after that the tab belongs to the user, not to the address bar.
  const [kind, setKind] = useState<Kind>(
    () => KIND_BY_PARAM[searchParams.get('loai') ?? ''] ?? 'expense',
  );
  const [notice, setNotice] = useState<string | null>(null);
  /** Bumped after every save so the form remounts with empty fields. */
  const [formKey, setFormKey] = useState(0);

  function close() {
    // Back where the ⊕ was tapped; a deep link straight to /them has nowhere to go back to.
    // react-router's navigate returns a promise for view transitions; nothing here waits on it.
    if (window.history.length > 1) void navigate(-1);
    else void navigate('/');
  }

  const header = (
    <header className={styles.header}>
      <button type="button" className={styles.close} aria-label="Đóng" onClick={close}>
        <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true" focusable="false">
          <g fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round">
            <path d="M6 6l12 12M18 6 6 18" />
          </g>
        </svg>
      </button>
      <h1 className={styles.title}>Thêm giao dịch</h1>
    </header>
  );

  if (contextState.status === 'loading' || optionsState.status === 'loading') {
    return (
      <div className={styles.sheet}>
        {header}
        <LoadingState label="Đang mở màn nhập nhanh…" />
      </div>
    );
  }
  if (contextState.status === 'error') {
    return (
      <div className={styles.sheet}>
        {header}
        <ErrorState message={contextState.message} onRetry={contextState.retry} />
      </div>
    );
  }
  if (optionsState.status === 'error') {
    return (
      <div className={styles.sheet}>
        {header}
        <ErrorState message={optionsState.message} onRetry={optionsState.retry} />
      </div>
    );
  }

  const { context } = contextState;
  // Captured after the guards above: a hoisted function declaration is type-checked without
  // the narrowing those guards produce, so the ready-state values are read here.
  const { options, refresh: refreshQuickAddOptions } = optionsState;
  const subjects = {
    selfSubjectId: context.selfSubject.id,
    householdSubjectId: context.householdSubject.id,
  };

  /** The entry is on the books at this point, so what it changed is reread (skill 11.1). */
  async function handleSaved(message: string) {
    setNotice(message);
    setFormKey((key) => key + 1);
    await mutate(ACCOUNTS_KEY);
  }

  /** A category created inside the form: reread the options AND the category screen's list. */
  async function refreshOptions() {
    await Promise.all([refreshQuickAddOptions(), mutate(CATEGORIES_KEY)]);
  }

  return (
    <div className={styles.sheet}>
      {header}

      <div className={styles.body}>
        <div className={styles.tabs} role="tablist" aria-label="Loại giao dịch">
          {TABS.map(([value, label]) => (
            <button
              key={value}
              type="button"
              role="tab"
              aria-selected={kind === value}
              className={cx(styles.tab, kind === value && styles.tabActive)}
              onClick={() => {
                setKind(value);
                setNotice(null);
              }}
            >
              {label}
            </button>
          ))}
        </div>

        {notice ? (
          <p className={forms.success} role="status">
            {notice}
          </p>
        ) : null}

        {kind === 'expense' ? (
          <ExpenseForm
            key={`expense-${formKey}`}
            options={options}
            subjects={subjects}
            householdId={context.household.id}
            timeZone={context.household.timezone}
            isFamily={context.isFamily}
            onSaved={(message) => void handleSaved(message)}
            onOptionsChanged={refreshOptions}
          />
        ) : null}

        {kind === 'income' ? (
          <IncomeForm
            key={`income-${formKey}`}
            options={options}
            subjects={subjects}
            timeZone={context.household.timezone}
            onSaved={(message) => void handleSaved(message)}
          />
        ) : null}

        {kind === 'transfer' ? (
          <TransferForm
            key={`transfer-${formKey}`}
            options={options}
            subjects={subjects}
            timeZone={context.household.timezone}
            onSaved={(message) => void handleSaved(message)}
          />
        ) : null}

        {kind === 'split' ? (
          <SplitForm
            key={`split-${formKey}`}
            options={options}
            subjects={subjects}
            timeZone={context.household.timezone}
            onSaved={(message) => void handleSaved(message)}
          />
        ) : null}
      </div>
    </div>
  );
}
